/* Capacitaciones Administrativas - detalle del anfitrión (TD-11629).
 *
 * - QR rotativo (cambia cada 30 s) para que los participantes registren su
 *   asistencia escaneándolo. Tiene modo pantalla completa para proyectarlo o
 *   compartir pantalla en una capacitación virtual. Mientras el QR está visible,
 *   la lista de participantes se refresca sola.
 * - Carga manual por nombre / cédula / legajo: quedan PENDIENTES hasta que
 *   cada persona confirme desde su app (recibe un push).
 * - Cerrar registro congela el QR y habilita la evaluación; las cargas manuales
 *   pendientes todavía se pueden confirmar.
 * - En BORRADOR no hay QR ni carga manual: solo editar, publicar o descartar.
 */
import {
  IonBadge,
  IonButton,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  useIonAlert,
  useIonToast,
} from "@ionic/react";
import { CircularProgress, TextField } from "@material-ui/core";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import {
  arrowBack,
  calendarOutline,
  closeCircleOutline,
  closeOutline,
  createOutline,
  expandOutline,
  locationOutline,
  peopleOutline,
  qrCodeOutline,
  timeOutline,
} from "ionicons/icons";
import { QRCodeCanvas } from "qrcode.react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Header from "src/components/Header";
import CapacitacionAdmAPI, {
  CapacitacionDetalle,
  EstadoCapacitacion,
  EstadoParticipante,
  Funcionario,
} from "src/services/capacitacionAdm";
import {
  ESTADO_CAPACITACION,
  ESTADO_PARTICIPANTE,
  ETIQUETA_MODALIDAD,
  colorPuntaje,
  formatFechaHora,
} from "src/utils/capacitacionAdm";
import "./CapacitacionAdm.css";

const REFRESCO_PARTICIPANTES_MS = 8000;

type FiltroParticipantes = "TODOS" | EstadoParticipante;

const CapacitacionAdmDetalle: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const capacitacionId = Number(id);
  const history = useHistory();
  const [present] = useIonToast();
  const [presentAlert] = useIonAlert();

  const [cap, setCap] = useState<CapacitacionDetalle | null>(null);
  const [cargando, setCargando] = useState(true);
  const [trabajando, setTrabajando] = useState(false);
  const [filtro, setFiltro] = useState<FiltroParticipantes>("TODOS");

  // QR
  const [mostrarQR, setMostrarQR] = useState(false);
  const [pantallaCompleta, setPantallaCompleta] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [restante, setRestante] = useState(0);
  const [ventana, setVentana] = useState(30);
  const qrTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Evita que una respuesta en vuelo reprograme el QR después de ocultarlo.
  const qrActivo = useRef(false);

  // Carga manual
  const [busqueda, setBusqueda] = useState("");
  const [opciones, setOpciones] = useState<Funcionario[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [seleccion, setSeleccion] = useState<Funcionario[]>([]);

  const cargar = async (silencioso = false) => {
    try {
      setCap(await CapacitacionAdmAPI.obtener(capacitacionId));
    } catch (err: any) {
      if (!silencioso) {
        present({ message: err?.response?.data?.error || "No se pudo cargar", duration: 3500, color: "danger" });
        history.replace("/capacitaciones-adm");
      }
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacitacionId]);

  // ------------------------- QR rotativo -------------------------
  const pedirQR = async () => {
    try {
      const res = await CapacitacionAdmAPI.qr(capacitacionId);
      if (!qrActivo.current) return;
      setQr(res.qr);
      setRestante(res.expira_en);
      setVentana(res.ventana_segundos || 30);
      // Se pide el siguiente apenas cambia la ventana.
      qrTimer.current = setTimeout(pedirQR, Math.max(1, res.expira_en) * 1000 + 300);
    } catch (err: any) {
      if (!qrActivo.current) return;
      present({ message: err?.response?.data?.error || "No se pudo generar el QR", duration: 3500, color: "danger" });
      setMostrarQR(false);
    }
  };

  useEffect(() => {
    qrActivo.current = mostrarQR;
    if (!mostrarQR) {
      if (qrTimer.current) clearTimeout(qrTimer.current);
      setQr(null);
      setPantallaCompleta(false);
      return;
    }
    pedirQR();
    const cuenta = setInterval(() => setRestante((r) => Math.max(0, r - 1)), 1000);
    const refresco = setInterval(() => cargar(true), REFRESCO_PARTICIPANTES_MS);
    return () => {
      qrActivo.current = false;
      if (qrTimer.current) clearTimeout(qrTimer.current);
      clearInterval(cuenta);
      clearInterval(refresco);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarQR]);

  // ------------------------- Búsqueda de funcionarios -------------------------
  useEffect(() => {
    const texto = busqueda.trim();
    if (texto.length < 3 && !/^\d+$/.test(texto)) {
      setOpciones([]);
      setBuscando(false);
      return;
    }
    let activo = true;
    setBuscando(true);
    const timer = setTimeout(async () => {
      try {
        const res = await CapacitacionAdmAPI.buscarFuncionarios(texto);
        if (activo) setOpciones(res);
      } catch {
        if (activo) setOpciones([]);
      } finally {
        if (activo) setBuscando(false);
      }
    }, 350);
    return () => {
      activo = false;
      clearTimeout(timer);
    };
  }, [busqueda]);

  const participantes = useMemo(() => cap?.participantes || [], [cap]);
  const cedulasEnLista = useMemo(() => new Set(participantes.map((p) => p.cedula)), [participantes]);

  const agregar = async () => {
    if (seleccion.length === 0) return;
    setTrabajando(true);
    try {
      const res = await CapacitacionAdmAPI.agregarParticipantes(
        capacitacionId,
        seleccion.map((s) => s.cedula)
      );
      let msg = res.info;
      if (res.ya_registrados.length) msg += ` · ${res.ya_registrados.length} ya estaba(n) en la lista`;
      if (res.no_encontrados.length) msg += ` · ${res.no_encontrados.length} no figura(n) como activo(s)`;
      present({ message: msg, duration: 4000, color: "success" });
      setSeleccion([]);
      setBusqueda("");
      await cargar(true);
    } catch (err: any) {
      present({ message: err?.response?.data?.error || "No se pudo agregar", duration: 4000, color: "danger" });
    } finally {
      setTrabajando(false);
    }
  };

  const quitar = (cedula: string, nombre: string | null) =>
    presentAlert({
      header: "Quitar participante",
      message: `¿Quitar a ${nombre || cedula} de la lista?`,
      buttons: [
        "Cancelar",
        {
          text: "Quitar",
          role: "destructive",
          handler: async () => {
            try {
              await CapacitacionAdmAPI.quitarParticipante(capacitacionId, cedula);
              await cargar(true);
            } catch (err: any) {
              present({ message: err?.response?.data?.error || "No se pudo quitar", duration: 3500, color: "danger" });
            }
          },
        },
      ],
    });

  const recordar = async () => {
    setTrabajando(true);
    try {
      const res = await CapacitacionAdmAPI.recordarPendientes(capacitacionId);
      present({ message: res.info, duration: 3000, color: "success" });
    } catch (err: any) {
      present({ message: err?.response?.data?.error || "No se pudo enviar", duration: 3500, color: "danger" });
    } finally {
      setTrabajando(false);
    }
  };

  // ------------------------- Estado -------------------------
  const cambiarEstado = async (estado: EstadoCapacitacion) => {
    setTrabajando(true);
    try {
      const res = await CapacitacionAdmAPI.cambiarEstado(capacitacionId, estado);
      if (estado !== "ABIERTA") setMostrarQR(false);
      present({ message: res.info, duration: 3000, color: "success" });
      await cargar(true);
    } catch (err: any) {
      present({ message: err?.response?.data?.error || "No se pudo cambiar el estado", duration: 3500, color: "danger" });
    } finally {
      setTrabajando(false);
    }
  };

  const confirmarCierre = () =>
    presentAlert({
      header: "Cerrar registro",
      message:
        "El QR deja de funcionar y se habilita la evaluación para quienes confirmaron (reciben un aviso). " +
        "Las cargas manuales pendientes todavía se pueden confirmar. Podés reabrirlo después.",
      buttons: ["Cancelar", { text: "Cerrar registro", handler: () => cambiarEstado("CERRADA") }],
    });

  const confirmarAnular = () =>
    presentAlert({
      header: cap?.estado === "BORRADOR" ? "Descartar borrador" : "Anular capacitación",
      message:
        cap?.estado === "BORRADOR"
          ? "El borrador queda descartado y no se puede recuperar. ¿Continuar?"
          : "Deja de mostrarse a los participantes y no se puede deshacer. ¿Continuar?",
      buttons: [
        "Cancelar",
        {
          text: cap?.estado === "BORRADOR" ? "Descartar" : "Anular",
          role: "destructive",
          handler: () => cambiarEstado("ANULADA"),
        },
      ],
    });

  // ------------------------- Render -------------------------
  if (cargando || !cap) {
    return (
      <IonPage>
        <Header titulo="Capacitación" botonIzq={{ icono: arrowBack, url: "/capacitaciones-adm" }} />
        <IonContent id="capacitaciones-adm-detalle">
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <CircularProgress />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const cuenta = (e: EstadoParticipante) => participantes.filter((p) => p.estado === e).length;
  const confirmados = cuenta("CONFIRMADO");
  const pendientes = cuenta("PENDIENTE");
  const rechazados = cuenta("RECHAZADO");
  const evaluados = participantes.filter((p) => p.fecha_evaluacion && p.puntaje !== null);
  const promedio = evaluados.length
    ? Math.round(evaluados.reduce((s, p) => s + Number(p.puntaje), 0) / evaluados.length)
    : null;
  const anulada = cap.estado === "ANULADA";
  const borrador = cap.estado === "BORRADOR";
  const publicada = !anulada && !borrador;
  const faltaParaPublicar = !cap.fecha_capacitacion || !cap.duracion_minutos;
  const visibles = filtro === "TODOS" ? participantes : participantes.filter((p) => p.estado === filtro);

  const filtros: Array<{ valor: FiltroParticipantes; etiqueta: string }> = [
    { valor: "TODOS", etiqueta: `Todos (${participantes.length})` },
    { valor: "CONFIRMADO", etiqueta: `Confirmaron (${confirmados})` },
    { valor: "PENDIENTE", etiqueta: `Pendientes (${pendientes})` },
    { valor: "RECHAZADO", etiqueta: `No participaron (${rechazados})` },
  ];

  const qrVisual = (tamano: number) =>
    qr ? (
      <div className="capadm-qr-caja">
        <QRCodeCanvas value={qr} size={tamano} level="M" includeMargin={false} />
      </div>
    ) : (
      <div style={{ padding: 40 }}>
        <CircularProgress />
      </div>
    );

  const barraQR = (
    <>
      <div className="capadm-qr-barra">
        <div style={{ width: `${Math.min(100, (restante / ventana) * 100)}%` }} />
      </div>
      <span style={{ fontSize: 12, color: "#6b7280" }}>
        Se renueva en {restante} s · {confirmados} confirmado(s)
      </span>
    </>
  );

  return (
    <IonPage>
      <Header
        titulo="Capacitación"
        botonIzq={{ icono: arrowBack, url: "/capacitaciones-adm" }}
        botonesDerecha={anulada ? undefined : [{ icono: createOutline, url: `/capacitaciones-adm/${cap.id}/editar` }]}
      />
      <IonContent id="capacitaciones-adm-detalle">
        <div className="capadm-page capadm-scope campos-mui">
          {/* ---------- Cabecera ---------- */}
          <div className="capadm-card">
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
              <p className="capadm-card-titulo" style={{ fontSize: 18 }}>
                {cap.tema}
              </p>
              <IonBadge color={ESTADO_CAPACITACION[cap.estado].color}>{ESTADO_CAPACITACION[cap.estado].etiqueta}</IonBadge>
            </div>
            <div className="capadm-meta">
              <span>
                <IonIcon icon={calendarOutline} /> {formatFechaHora(cap.fecha_capacitacion)}
              </span>
              {cap.duracion_minutos ? (
                <span>
                  <IonIcon icon={timeOutline} /> {cap.duracion_minutos} min
                </span>
              ) : null}
              <span>
                <IonIcon icon={locationOutline} /> {ETIQUETA_MODALIDAD[cap.modalidad]}
                {cap.lugar ? ` · ${cap.lugar}` : ""}
              </span>
            </div>
            {cap.departamentos.length > 0 && (
              <div className="capadm-meta">
                <span>
                  <IonIcon icon={peopleOutline} /> {cap.departamentos.join(", ")}
                </span>
              </div>
            )}
            {cap.descripcion && <p className="capadm-card-sub" style={{ margin: "10px 0 0" }}>{cap.descripcion}</p>}
            <p className="capadm-card-sub" style={{ margin: "10px 0 0" }}>
              Evaluación:{" "}
              {!cap.preguntas.length
                ? "sin preguntas"
                : `${cap.preguntas.length} pregunta(s) · ` +
                  (cap.evaluacion_disponible
                    ? "habilitada para los participantes"
                    : cap.fecha_fin
                    ? `se habilita al finalizar (${formatFechaHora(cap.fecha_fin)}) o al cerrar el registro`
                    : "se habilita al finalizar la capacitación")}
            </p>
          </div>

          {/* ---------- Borrador ---------- */}
          {borrador && (
            <div className="capadm-card" style={{ borderColor: "var(--ion-color-tertiary)" }}>
              <p className="capadm-card-titulo">Borrador</p>
              <p className="capadm-card-sub">
                Los participantes todavía no la ven. Al publicarla se habilitan el QR de asistencia y la carga de
                participantes.
                {faltaParaPublicar && " Para publicar falta completar la fecha, la hora y la duración."}
              </p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <IonButton fill="outline" style={{ flex: "1 1 140px" }} routerLink={`/capacitaciones-adm/${cap.id}/editar`}>
                  Editar
                </IonButton>
                <IonButton style={{ flex: "1 1 140px" }} disabled={trabajando || faltaParaPublicar}
                  onClick={() => cambiarEstado("ABIERTA")}>
                  Publicar
                </IonButton>
              </div>
            </div>
          )}

          {/* ---------- Indicadores ---------- */}
          {publicada && (
          <div className="capadm-stats">
            <div className="capadm-stat">
              <div className="capadm-stat-valor" style={{ color: "#2e7d32" }}>{confirmados}</div>
              <div className="capadm-stat-etiqueta">Confirmaron</div>
            </div>
            <div className="capadm-stat">
              <div className="capadm-stat-valor" style={{ color: "#b26a00" }}>{pendientes}</div>
              <div className="capadm-stat-etiqueta">Pendientes</div>
            </div>
            <div className="capadm-stat">
              <div className="capadm-stat-valor">
                {cap.preguntas.length ? `${evaluados.length}/${confirmados}` : "—"}
              </div>
              <div className="capadm-stat-etiqueta">Evaluados</div>
            </div>
            <div className="capadm-stat">
              <div className="capadm-stat-valor" style={promedio !== null ? { color: colorPuntaje(promedio) } : undefined}>
                {promedio !== null ? `${promedio}%` : "—"}
              </div>
              <div className="capadm-stat-etiqueta">Promedio</div>
            </div>
          </div>
          )}

          {/* ---------- QR ---------- */}
          {cap.estado === "ABIERTA" && (
            <div className="capadm-card" style={{ textAlign: "center" }}>
              {!mostrarQR ? (
                <>
                  <p className="capadm-card-titulo">Registro con QR</p>
                  <p className="capadm-card-sub">
                    Mostralo en la capacitación o compartí pantalla si es virtual. Los participantes lo escanean desde
                    Menú → <strong>Mis capacitaciones</strong>.
                  </p>
                  <IonButton expand="block" onClick={() => setMostrarQR(true)}>
                    <IonIcon slot="start" icon={qrCodeOutline} /> Mostrar QR de asistencia
                  </IonButton>
                </>
              ) : (
                <>
                  {qrVisual(240)}
                  {barraQR}
                  <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 12, flexWrap: "wrap" }}>
                    <IonButton size="small" onClick={() => setPantallaCompleta(true)} disabled={!qr}>
                      <IonIcon slot="start" icon={expandOutline} /> Pantalla completa
                    </IonButton>
                    <IonButton size="small" fill="outline" onClick={() => setMostrarQR(false)}>
                      Ocultar QR
                    </IonButton>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ---------- Carga manual ---------- */}
          {publicada && (
            <div className="capadm-card">
              <p className="capadm-card-titulo">Agregar participantes</p>
              <p className="capadm-card-sub">
                Para quienes no escanearon el QR. Quedan pendientes hasta que cada persona confirme desde su app.
              </p>
              <MuiAutocomplete
                multiple
                options={opciones.filter((o) => !cedulasEnLista.has(o.cedula))}
                value={seleccion}
                onChange={(_, val) => setSeleccion(val)}
                getOptionLabel={(o) => o.nombre}
                getOptionSelected={(a, b) => a.cedula === b.cedula}
                renderOption={(o) => (
                  <div>
                    <div style={{ fontSize: 14 }}>{o.nombre}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      CI {o.cedula}
                      {o.legajo ? ` · Leg. ${o.legajo}` : ""} · {o.cargo}
                      {o.departamento ? ` · ${o.departamento}` : ""}
                    </div>
                  </div>
                )}
                inputValue={busqueda}
                onInputChange={(_, val, reason) => {
                  if (reason !== "reset") setBusqueda(val);
                }}
                filterOptions={(opts) => opts}
                loading={buscando}
                loadingText="Buscando..."
                noOptionsText={
                  busqueda.trim().length < 3 && !/^\d+$/.test(busqueda.trim())
                    ? "Escribí nombre, cédula o legajo"
                    : "Sin coincidencias"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Nombre, cédula o legajo"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {buscando ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <IonButton expand="block" onClick={agregar} disabled={trabajando || seleccion.length === 0} style={{ marginTop: 10 }}>
                Agregar{seleccion.length ? ` (${seleccion.length})` : ""}
              </IonButton>
            </div>
          )}

          {/* ---------- Participantes ---------- */}
          {!borrador && (
          <div className="capadm-card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <p className="capadm-card-titulo" style={{ margin: 0 }}>Participantes</p>
              {pendientes > 0 && !anulada && (
                <IonButton size="small" fill="outline" onClick={recordar} disabled={trabajando}>
                  Recordar pendientes
                </IonButton>
              )}
            </div>

            {participantes.length === 0 ? (
              <div className="capadm-vacio">
                <IonIcon icon={peopleOutline} />
                Todavía no hay participantes.
              </div>
            ) : (
              <>
                <div className="capadm-filtros" style={{ marginTop: 12 }}>
                  {filtros.map((f) => (
                    <button
                      key={f.valor}
                      type="button"
                      className={`capadm-filtro${filtro === f.valor ? " activo" : ""}`}
                      onClick={() => setFiltro(f.valor)}
                    >
                      {f.etiqueta}
                    </button>
                  ))}
                </div>
                {visibles.length === 0 ? (
                  <div className="capadm-vacio" style={{ padding: 16 }}>Nadie en este estado.</div>
                ) : (
                  <IonList style={{ margin: "0 -16px" }}>
                    {visibles.map((p) => (
                      <IonItem key={p.cedula} lines="full">
                        <IonLabel style={{ whiteSpace: "normal" }}>
                          <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: "#222" }}>{p.nombre || p.cedula}</p>
                          <p style={{ margin: "2px 0 0", fontSize: 12 }}>
                            CI {p.cedula}
                            {p.legajo ? ` · Leg. ${p.legajo}` : ""}
                            {p.departamento ? ` · ${p.departamento}` : ""}
                          </p>
                          <p style={{ margin: "2px 0 0", fontSize: 12 }}>
                            {p.metodo === "QR" ? "Escaneó el QR" : "Carga manual"}
                            {p.fecha_respuesta ? ` · ${formatFechaHora(p.fecha_respuesta)}` : ""}
                          </p>
                        </IonLabel>
                        <div slot="end" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                          <IonBadge color={ESTADO_PARTICIPANTE[p.estado].color}>{ESTADO_PARTICIPANTE[p.estado].etiqueta}</IonBadge>
                          {p.fecha_evaluacion && p.puntaje !== null && (
                            <span style={{ fontSize: 12, fontWeight: 700, color: colorPuntaje(Number(p.puntaje)) }}>
                              {Math.round(Number(p.puntaje))}%
                            </span>
                          )}
                        </div>
                        {p.estado !== "CONFIRMADO" && !anulada && (
                          <IonButton slot="end" fill="clear" color="medium" onClick={() => quitar(p.cedula, p.nombre)}
                            aria-label="Quitar participante">
                            <IonIcon slot="icon-only" icon={closeCircleOutline} />
                          </IonButton>
                        )}
                      </IonItem>
                    ))}
                  </IonList>
                )}
              </>
            )}
          </div>

          )}

          {/* ---------- Acciones de estado ---------- */}
          {!anulada && (
            <div style={{ marginBottom: 24 }}>
              {borrador ? null : cap.estado === "ABIERTA" ? (
                <IonButton expand="block" fill="outline" onClick={confirmarCierre} disabled={trabajando}>
                  Cerrar registro de asistencia
                </IonButton>
              ) : (
                <IonButton expand="block" fill="outline" onClick={() => cambiarEstado("ABIERTA")} disabled={trabajando}>
                  Reabrir registro de asistencia
                </IonButton>
              )}
              <IonButton expand="block" fill="clear" color="danger" onClick={confirmarAnular} disabled={trabajando}>
                {borrador ? "Descartar borrador" : "Anular capacitación"}
              </IonButton>
            </div>
          )}
        </div>

        {/* ---------- QR pantalla completa (para proyectar / compartir pantalla) ---------- */}
        <IonModal isOpen={pantallaCompleta} onDidDismiss={() => setPantallaCompleta(false)}>
          <IonContent>
            <div className="capadm-qr-full">
              <IonButton fill="clear" color="medium" onClick={() => setPantallaCompleta(false)}
                style={{ position: "absolute", top: 8, right: 8 }} aria-label="Cerrar">
                <IonIcon slot="icon-only" icon={closeOutline} />
              </IonButton>
              <h2>{cap.tema}</h2>
              <p style={{ margin: "0 0 20px", color: "#4b5563" }}>
                Escaneá desde la app: Menú → <strong>Mis capacitaciones</strong>
              </p>
              {qrVisual(Math.round(Math.min(520, window.innerWidth * 0.8, window.innerHeight * 0.6)))}
              {barraQR}
            </div>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default CapacitacionAdmDetalle;
